import axios from "axios";
const axiosApiInstance = axios.create();

import { isProviderAccessRevokedError } from "@/helpers/authErrors.js";
import store from "@/store/index.js";
let refreshPromise = null;
const AUTH_BYPASS_PATHS = new Set([
  "/api/auth/login",
  "/api/auth/signup",
  "/api/auth/send_reset",
  "/api/auth/send_otp",
  "/api/auth/refresh",
  "/api/auth/reset",
  "/api/auth/verify-email-link",
  "/api/auth/verify_otp",
  "/api/auth/accept-invite",
]);

function parseStoredSession() {
  const raw = localStorage.getItem("enlazado_session");

  if (!raw || raw === "null") {
    return null;
  }

  try {
    return JSON.parse(raw);
  } catch (error) {
    console.log("Error parsing session:", error);
    return null;
  }
}

function getCurrentSession() {
  const inMemorySession = store?.state?.session || null;
  const storedSession = parseStoredSession();
  const inMemoryTokens = extractSessionTokens(inMemorySession);
  const storedTokens = extractSessionTokens(storedSession);

  if (inMemoryTokens.accessToken && inMemoryTokens.refreshToken) {
    return inMemorySession;
  }

  if (storedTokens.accessToken && storedTokens.refreshToken) {
    return storedSession;
  }

  if (inMemoryTokens.accessToken || inMemoryTokens.refreshToken) {
    return inMemorySession;
  }

  if (storedTokens.accessToken || storedTokens.refreshToken) {
    return storedSession;
  }

  return inMemorySession || storedSession || null;
}

function extractSessionTokens(session) {
  if (!session || typeof session !== "object") {
    return {
      accessToken: null,
      refreshToken: null,
    };
  }

  const nestedTokens = session?.user?.session;
  const directTokens = session?.session;

  return {
    accessToken:
      nestedTokens?.access_token ||
      directTokens?.access_token ||
      session?.access_token ||
      null,
    refreshToken:
      nestedTokens?.refresh_token ||
      directTokens?.refresh_token ||
      session?.refresh_token ||
      null,
  };
}

function maskToken(token) {
  if (!token || typeof token !== "string") {
    return null;
  }

  if (token.length <= 10) {
    return `${token.slice(0, 2)}...${token.slice(-2)}`;
  }

  return `${token.slice(0, 6)}...${token.slice(-4)} (${token.length})`;
}

function summarizeSession(session) {
  const { accessToken, refreshToken } = extractSessionTokens(session);

  return {
    accessToken: maskToken(accessToken),
    refreshToken: maskToken(refreshToken),
    providerCount: Array.isArray(session?.providers) ? session.providers.length : 0,
    userId: session?.user?.user?.id || session?.user?.id || null,
  };
}

function persistRefreshedTokens(nextSessionTokens) {
  if (
    !nextSessionTokens?.access_token ||
    !nextSessionTokens?.refresh_token
  ) {
    return false;
  }

  const session = getCurrentSession() || {};
  if (!session.user || typeof session.user !== "object") {
    session.user = {};
  }

  session.user.session = {
    ...(session.user.session || {}),
    access_token: nextSessionTokens.access_token,
    refresh_token: nextSessionTokens.refresh_token,
  };

  session.access_token = nextSessionTokens.access_token;
  session.refresh_token = nextSessionTokens.refresh_token;

  store.commit("setSession", session);
  localStorage.setItem("enlazado_session", JSON.stringify(session));
  console.info("[auth-debug] persistRefreshedTokens.success", {
    session: summarizeSession(session),
  });

  return true;
}

function redirectToLogin() {
  console.warn("[auth-debug] redirectToLogin", {
    pathname: window.location.pathname,
  });
  if (window.location.pathname === "/login") {
    return;
  }

  window.location.replace("/login");
}

function clearSession() {
  console.warn("[auth-debug] clearSession", {
    inMemory: summarizeSession(store?.state?.session),
    stored: summarizeSession(parseStoredSession()),
  });
  store.commit("setSession", null);
  localStorage.setItem("enlazado_session", "null");
}

function getRequestPath(config) {
  const url = config?.url;
  if (typeof url !== "string") {
    return "";
  }

  return url.split("?")[0];
}

function shouldBypassAuth(config) {
  const path = getRequestPath(config);
  return AUTH_BYPASS_PATHS.has(path);
}

async function refreshAuthSession() {
  if (refreshPromise) {
    return refreshPromise;
  }

  const session = getCurrentSession();
  const { refreshToken } = extractSessionTokens(session);

  console.info("[auth-debug] refreshAuthSession.start", {
    session: summarizeSession(session),
  });

  if (!refreshToken) {
    console.error("[auth-debug] refreshAuthSession.missing_refresh_token");
    throw new Error("Missing refresh token");
  }

  refreshPromise = axios
    .post("/api/auth/refresh", {
      refresh_token: refreshToken,
    })
    .then((res) => {
      console.info("[auth-debug] refreshAuthSession.response", {
        status: res?.status || null,
        dataSession: {
          accessToken: maskToken(res?.data?.session?.access_token),
          refreshToken: maskToken(res?.data?.session?.refresh_token),
        },
      });
      const refreshedSession = res?.data?.session;
      const wasPersisted = persistRefreshedTokens(refreshedSession);

      if (!wasPersisted) {
        throw new Error("Unable to persist refreshed session");
      }
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

axiosApiInstance.interceptors.request.use(
  (config) => {
    if (shouldBypassAuth(config)) {
      return config;
    }

    const session = getCurrentSession();
    const { accessToken } = extractSessionTokens(session);

    if (accessToken) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${accessToken}`;
      config.headers.Accept = "application/json";
    }

    console.info("[auth-debug] axios.request", {
      url: config?.url || null,
      method: config?.method || null,
      bypass: shouldBypassAuth(config),
      accessToken: maskToken(accessToken),
    });

    return config;
  },
  (error) => {
    console.log("Request error:", error);
    return Promise.reject(error);
  }
);

axiosApiInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    console.log("Response error:", error);
    console.warn("[auth-debug] axios.response_error", {
      url: error?.config?.url || null,
      method: error?.config?.method || null,
      status: error?.response?.status || null,
      data: error?.response?.data || null,
      isRetry: Boolean(error?.config?._retry),
    });

    if (isProviderAccessRevokedError(error)) {
      console.warn("[auth-debug] axios.provider_access_revoked");
      clearSession();
      redirectToLogin();
      return Promise.reject(error);
    }

    if (error?.response?.status !== 401) {
      return Promise.reject(error);
    }

    const config = error?.config || {};

    if (shouldBypassAuth(config)) {
      return Promise.reject(error);
    }

    if (config._retry || config.url === "/api/auth/refresh") {
      console.warn("[auth-debug] axios.auth_retry_failed", {
        url: config.url || null,
      });
      clearSession();
      redirectToLogin();
      return Promise.reject(error);
    }

    config._retry = true;

    try {
      await refreshAuthSession();
      console.info("[auth-debug] axios.retry_after_refresh", {
        url: config?.url || null,
      });
      return axiosApiInstance.request(config);
    } catch (refreshError) {
      console.error("[auth-debug] axios.refresh_failed", {
        error: refreshError?.message || refreshError,
      });
      clearSession();
      redirectToLogin();
      return Promise.reject(refreshError);
    }
  }
);

export default axiosApiInstance;
